import moment from 'moment';
import { isEmpty } from 'lodash';

import { connect } from 'react-redux';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import ShieldCheckImg from '@assets/images/insurance/shield-check.svg';

import { INSURANCE_AVAILABLE_STATES } from '@constants';
import { Routes } from '@share/constants';
import { BlueButton } from '@share/components';
import { ICondosStaticState, ILoginState, IMenuState } from '@share/store/slices';
import { IRciCountriesCodes } from '@share/common-types';
import { GetDisableNewTabOpen, RootState, roundMoney2, scrollTop } from '@share/utils';
import { IInsuranceComplete } from '@common-types';
import { IInsuranceState, InsuranceSelection } from '@store/slices';

import { Insurance } from '../insurance-wrapper';

import './style.scss';

const DATE_FORMAT = 'yyyy-MM-DD';
const MIN_TRAVELERS = 1;
const MAX_TRAVELERS = 50;
/** Condo bookings store the country as "USA" where hotels store "US". */
const US_COUNTRY_ALIASES = ['US', 'USA', 'UNITED STATES'];

interface IMapStateToProps {
  loginStore: ILoginState;
  menuStore: IMenuState;
  insuranceStore: IInsuranceState;
  condosStaticStore: ICondosStaticState;
}

interface IProps extends IMapStateToProps, RouteComponentProps {
  /** Booking guid, for attribution only. */
  bookingId?: string;
  checkIn?: string;
  checkOut?: string;
  /** Destination country, either an ISO-2 code or a country name. */
  destinationCountry?: string;
  travelers?: number;
  tripCost?: number;
  /** Residency state of the main guest / card holder. */
  residencyState?: string;
  /** Residency country of the main guest / card holder. Only US residents can be insured. */
  residencyCountry?: string;
  /** Policy created within this booking, when any. */
  insurance?: IInsuranceComplete;
}

const formatDate = (date?: string): string | null => {
  if (!date) {
    return null;
  }

  const parsed = moment(date, DATE_FORMAT);

  return parsed.isValid() ? parsed.format(DATE_FORMAT) : null;
};

const getIsoCountryCode = (
  destinationCountry?: string,
  rciCountries?: IRciCountriesCodes[],
): string | null => {
  if (!destinationCountry || !rciCountries?.length) {
    return null;
  }

  const value = destinationCountry.trim().toLowerCase();
  const country = rciCountries.find(
    (c: IRciCountriesCodes) =>
      c?.isoCode?.toLowerCase() === value || c?.name?.toLowerCase() === value,
  );

  return country?.isoCode || null;
};

const normalizeCode = (value?: string): string | null => {
  const code = value?.trim().toUpperCase();

  return code ? code : null;
};

function InsuranceCrossSellComponent(props: IProps) {
  const {
    bookingId,
    checkIn,
    checkOut,
    destinationCountry,
    travelers,
    tripCost,
    residencyState,
    residencyCountry,
    insurance,
    loginStore,
    menuStore,
    insuranceStore,
    condosStaticStore,
    history,
  } = props;

  const { account } = loginStore;
  const { items } = menuStore;
  const { selection, insurance: quote, travelersRequest, loading, error } = insuranceStore;
  const { condosCountriesCode } = condosStaticStore;

  if (!account?.name || !account?.enableInsurance || items?.isRemoveInsurance) {
    return null;
  }

  const isEnabledForMember =
    account?.insuranceCrossSell &&
    ((!account?.insuranceCrossSellSiteidList?.length && !account?.insuranceCrossSellOrgidList?.length) ||
      (account?.insuranceCrossSellSiteidList?.includes(items?.siteId?.toString() as string) ||
        account?.insuranceCrossSellOrgidList?.includes(items?.organizationId?.toString() as string)));

  if (!isEnabledForMember) {
    return null;
  }

  // Insurance already bought within this booking.
  if (selection === InsuranceSelection.YES || insurance?.policyDetail) {
    return null;
  }

  /* Residency is only known for sure while the member is still on the review page: a
     completed hotel booking resets the card and clears the stored billing address. So these
     two checks only reject a residency we positively know is ineligible — an unknown one
     falls through to the quote check below, which is the provider's own verdict. */
  const country = normalizeCode(residencyCountry);
  const isUsResident = !country || US_COUNTRY_ALIASES.includes(country);

  if (!isUsResident) {
    return null;
  }

  const state = normalizeCode(residencyState || travelersRequest?.residency?.isoStateOrProvince);

  if (state && !INSURANCE_AVAILABLE_STATES.includes(state)) {
    return null;
  }

  /* A quote exists only when the member could actually be insured: it is requested solely
     for US residents on accounts with insurance enabled, and it fails for states the
     provider does not underwrite. It also carries the price the offer displays. */
  if (loading || !isEmpty(error) || !quote?.productDetails?.price) {
    return null;
  }

  const getSearchParams = (): string => {
    const params = new URLSearchParams();

    if (bookingId) {
      params.set('bookingId', bookingId);
    }

    const from = formatDate(checkIn);
    const to = formatDate(checkOut);

    if (from && to && moment(to).isAfter(moment(from))) {
      params.set('from', from);
      params.set('to', to);
    }

    const country = getIsoCountryCode(destinationCountry, condosCountriesCode?.rciCountries);

    if (country) {
      params.set('country', country);
    }

    if (state && INSURANCE_AVAILABLE_STATES.includes(state)) {
      params.set('state', state);
    }

    if (travelers && travelers >= MIN_TRAVELERS && travelers <= MAX_TRAVELERS) {
      params.set('travelers', travelers.toString());
    }

    if (tripCost && tripCost > 0) {
      params.set('tripCost', roundMoney2(tripCost).toString());
    }

    return params.toString();
  };

  const handleGoToInsurance = () => {
    const search = getSearchParams();
    const path = `/${account.name}${Routes.InsuranceSearch}${search ? `?${search}` : ''}`;

    /* A new tab by default so the member keeps their confirmation — losing it right after
       booking would be worse than the extra tab. Accounts that opt out of new tabs (and
       iframe embeds, where a popup is blocked or breaks out of the frame) navigate in place
       and need the scroll reset, since a push keeps the current offset. */
    if (account?.forceIframe || GetDisableNewTabOpen(account, items)) {
      history.push(path);
      scrollTop();

      return;
    }

    window.open(path, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="insurance-cross-sell">
      <div className="insurance-cross-sell__header">
        <div className="insurance-cross-sell__content">
          <img src={ShieldCheckImg} className="insurance-cross-sell__icon" alt="shield_check" />
          <div className="insurance-cross-sell__text">
            <p className="insurance-cross-sell__title">
              <FormattedMessage id="insurance.crossSell.title" />
            </p>
            <p className="insurance-cross-sell__message">
              <FormattedMessage id="insurance.crossSell.message" />
            </p>
          </div>
        </div>
        <div className="insurance-cross-sell__btn-wrapper">
          <BlueButton onClick={handleGoToInsurance}>
            <FormattedMessage id="insurance.crossSell.cta" />
          </BlueButton>
        </div>
      </div>

      <Insurance crossSell />
    </div>
  );
}

const mapStateToProps = (state: RootState): IMapStateToProps => {
  return {
    loginStore: state.loginStore,
    menuStore: state.navigationMenuStore,
    insuranceStore: state.insuranceStore,
    condosStaticStore: state.condosStaticStore,
  };
};

export const InsuranceCrossSell = connect(mapStateToProps)(
  withRouter(InsuranceCrossSellComponent),
);
